import React, { useMemo, useState } from 'react';
import type { ApexOptions } from 'apexcharts';
import { Switch, Tooltip, Typography } from 'antd';
import Chart from 'react-apexcharts';

import {
  anchorIndex,
  curveAxis,
  levelsDiffer,
  NORMALIZE_RATIO,
  rebaseTo100,
  seriesLevel,
} from './format';
import { Curves, CurveSeries, Instrument, Preview } from './types';

type Props = {
  curves: Curves;
  turnWindow: Preview['window'];
  /** The preview's instruments: a curve without a known axis goes by its kind. */
  instruments?: Instrument[];
};

type Point = { x: number; y: number | null };

const finiteOrNull = (value: number | null | undefined) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Decimals that suit a price's size: 1.0802, 17.963, 385.56, 30125.5. */
const priceDecimals = (value: number) => {
  const size = Math.abs(value);
  if (size >= 1000) return 1;
  if (size >= 100) return 2;
  if (size >= 10) return 3;
  return 4;
};

const formatPrice = (value: number | null | undefined) =>
  typeof value === 'number' && Number.isFinite(value)
    ? value.toFixed(priceDecimals(value))
    : '—';
const formatRebased = (value: number | null | undefined) =>
  typeof value === 'number' && Number.isFinite(value) ? value.toFixed(1) : '—';
const formatYield = (value: number | null | undefined) =>
  typeof value === 'number' && Number.isFinite(value)
    ? `${value.toFixed(2)}%`
    : '—';
const formatSpread = (value: number | null | undefined) =>
  typeof value === 'number' && Number.isFinite(value)
    ? `${Math.round(value)} bp`
    : '—';

const BAND_COLOUR = '#3768d4';
const LANDING_COLOUR = '#8c98ab';

const NORMALIZE_LABEL = 'Normalize (rebased to 100)';
const REBASED_TITLE = 'Rebased (window start = 100)';

/**
 * The window's curves: prices on the left axis, bond yields in percent on the
 * right (`opposite`), after `InstrumentPriceChart`. Any instrument that is not
 * a bond (FX, ETF, equity, commodity, crypto, a kind not known yet) is a
 * price. Price lines of very different levels (USD/TRY ~30 beside EUR/USD
 * ~1.1) can be rebased to 100 at the window start so they compare in percent;
 * that is on by default when their levels differ more than NORMALIZE_RATIO
 * times, until the admin picks. Yields stay in percent. Turns are alternating
 * shaded bands between landings with a dashed line at each landing, after
 * Market Explorer's `PriceChart` annotations. Spreads, in bp, are optional
 * and get an axis of their own.
 */
const CurvesChart: React.FC<Props> = ({ curves, turnWindow, instruments }) => {
  const [showSpreads, setShowSpreads] = useState(false);
  /** The admin's own pick; until there is one the levels decide. */
  const [normalizeChoice, setNormalizeChoice] = useState<boolean | null>(null);
  const hasSpreads = (curves.spreads || []).length > 0;

  const placed = useMemo(() => {
    const kinds = new Map(
      (instruments || []).map(instrument => [instrument.code, instrument.kind]),
    );
    const price: CurveSeries[] = [];
    const yields: CurveSeries[] = [];
    (curves.series || []).forEach(series => {
      if (curveAxis(series, kinds.get(series.code)) === 'yield')
        yields.push(series);
      else price.push(series);
    });
    return { price, yields };
  }, [curves, instruments]);

  const canNormalize =
    placed.price.filter(series => seriesLevel(series.values) != null).length >
    1;
  const levelsApart = useMemo(
    () => levelsDiffer(placed.price.map(series => series.values)),
    [placed],
  );
  const normalized = canNormalize && (normalizeChoice ?? levelsApart);

  const chart = useMemo(() => {
    const dates = curves.dates || [];
    const xs = dates.map(date => Date.parse(date));
    const toPoints = (values: Array<number | null>): Point[] =>
      xs.map((x, index) => ({ x, y: finiteOrNull(values?.[index]) }));
    const anchor = anchorIndex(dates, turnWindow.start);
    const price = placed.price.map(series => ({
      name: series.label || series.code,
      type: 'line' as const,
      data: toPoints(
        normalized ? rebaseTo100(series.values, anchor) : series.values,
      ),
    }));
    const yields = placed.yields.map(series => ({
      name: `${series.label || series.code} (%)`,
      type: 'line' as const,
      data: toPoints(series.values),
    }));
    const spreads = (showSpreads ? curves.spreads || [] : []).map(series => ({
      name: `${series.label || series.code} (bp)`,
      type: 'line' as const,
      data: toPoints(series.values),
    }));
    // The real levels, so a rebased line's tooltip still names its price.
    const levels = placed.price.map(series => series.values || []);
    return { price, yields, spreads, levels };
  }, [curves, placed, normalized, showSpreads, turnWindow.start]);

  const options = useMemo<ApexOptions>(() => {
    const priceNames = chart.price.map(series => series.name);
    const yieldNames = chart.yields.map(series => series.name);
    const spreadNames = chart.spreads.map(series => series.name);
    const yaxis: ApexYAxis[] = [];
    if (priceNames.length) {
      yaxis.push({
        seriesName: priceNames,
        forceNiceScale: true,
        title: {
          text: normalized
            ? REBASED_TITLE
            : priceNames.length === 1
              ? priceNames[0]
              : 'Price',
        },
        labels: { formatter: normalized ? formatRebased : formatPrice },
      });
    }
    if (yieldNames.length) {
      yaxis.push({
        seriesName: yieldNames,
        opposite: true,
        forceNiceScale: true,
        decimalsInFloat: 2,
        title: { text: 'Yield (%)' },
        labels: { formatter: formatYield },
      });
    }
    if (spreadNames.length) {
      yaxis.push({
        seriesName: spreadNames,
        opposite: true,
        forceNiceScale: true,
        title: { text: 'Spread (bp)' },
        labels: { formatter: formatSpread },
      });
    }
    const rebasedTooltip =
      (levels: Array<number | null>) =>
      (value: number, context?: { dataPointIndex?: number }) => {
        const index = context?.dataPointIndex;
        const level = typeof index === 'number' ? levels[index] : null;
        return level != null && Number.isFinite(level)
          ? `${formatRebased(value)} (${formatPrice(level)})`
          : formatRebased(value);
      };

    // `landings` starts with the window start (the Friday before turn 1), so the
    // unique ordered boundaries are the turn edges: turn k spans boundary k-1..k.
    const boundaries = Array.from(
      new Set([turnWindow.start, ...turnWindow.landings]),
    ).map(date => Date.parse(date));
    const bands = boundaries.slice(1).map((end, index) => ({
      x: boundaries[index],
      x2: end,
      fillColor: BAND_COLOUR,
      opacity: index % 2 === 0 ? 0.07 : 0.015,
      borderWidth: 0,
      label: {
        text: `T${index + 1}`,
        borderWidth: 0,
        orientation: 'horizontal',
        position: 'top',
        style: {
          background: 'transparent',
          color: '#68758a',
          fontSize: '10px',
        },
      },
    }));
    const landings = turnWindow.landings.map(landing => ({
      x: Date.parse(landing),
      strokeDashArray: 4,
      borderColor: LANDING_COLOUR,
    }));

    return {
      chart: {
        animations: { enabled: false },
        fontFamily: 'inherit',
        foreColor: '#68758a',
        parentHeightOffset: 0,
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      dataLabels: { enabled: false },
      grid: {
        borderColor: '#edf0f5',
        strokeDashArray: 4,
        padding: { top: 8, left: 4, right: 4, bottom: 0 },
      },
      legend: { fontSize: '11px', horizontalAlign: 'left', position: 'top' },
      markers: { size: 0 },
      stroke: { curve: 'straight', width: 2 },
      tooltip: {
        shared: true,
        x: { format: 'dd MMM yyyy' },
        y: [
          ...chart.levels.map(levels => ({
            formatter: normalized ? rebasedTooltip(levels) : formatPrice,
          })),
          ...yieldNames.map(() => ({ formatter: formatYield })),
          ...spreadNames.map(() => ({ formatter: formatSpread })),
        ],
      },
      xaxis: {
        type: 'datetime',
        labels: { datetimeUTC: true },
        tooltip: { enabled: false },
      },
      yaxis,
      annotations: { xaxis: [...bands, ...landings] },
    };
  }, [chart, normalized, turnWindow]);

  const series = [...chart.price, ...chart.yields, ...chart.spreads];
  const chartable = series.some(line =>
    line.data.some(point => point.y != null),
  );
  const axes = [
    chart.price.length
      ? normalized
        ? 'Prices rebased to 100 at the window start on the left axis'
        : 'Prices on the left axis'
      : '',
    chart.yields.length ? 'yields (%) on the right' : '',
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="blueprint-builder__curves">
      <div className="blueprint-builder__curves-head">
        <Typography.Text type="secondary">
          {axes ? `${axes.charAt(0).toUpperCase()}${axes.slice(1)}. ` : ''}
          Shaded bands are turns; dashed lines are landings.
        </Typography.Text>
        <div className="blueprint-builder__curves-switches">
          {canNormalize && (
            <Tooltip
              title={`Each price line divided by its level at the window start, times 100, so lines of any size compare in percent. On by default when their levels differ more than ${NORMALIZE_RATIO}×.`}
            >
              <span className="blueprint-builder__switch">
                <Switch
                  size="small"
                  checked={normalized}
                  onChange={setNormalizeChoice}
                  aria-label={NORMALIZE_LABEL}
                />{' '}
                {NORMALIZE_LABEL}
              </span>
            </Tooltip>
          )}
          {hasSpreads && (
            <span className="blueprint-builder__switch">
              <Switch
                size="small"
                checked={showSpreads}
                onChange={setShowSpreads}
                aria-label="Show spreads"
              />{' '}
              Spreads (bp)
            </span>
          )}
        </div>
      </div>
      {chartable ? (
        <Chart height={340} options={options} series={series} type="line" />
      ) : (
        <Typography.Text type="secondary">
          No curve data for this window.
        </Typography.Text>
      )}
    </div>
  );
};

export default CurvesChart;
